/* eslint-disable react/prop-types */
// Funeral notice card — classical printed-style layout matching real "manifesto funebre".
// Pure presentational. All fields optional, rendered conditionally.

const BADGE_URL =
  "https://customer-assets.emergentagent.com/job_agenzia-funebre/artifacts/qttlf4no_badge.png";

export default function ManifestoCard({ data, compact = false }) {
  if (!data) return null;
  const fullName = [data.name_first, data.name_last].filter(Boolean).join(" ");
  const hasDecoration = !!data.decoration_url;

  return (
    <article
      className="bg-white border border-[color:var(--afb-cream)] shadow-sm rounded-md overflow-hidden"
      data-testid={`manifesto-card-${data.id}`}
    >
      <div className="border-2 border-[color:var(--afb-cream)] m-2 sm:m-3 relative">
        <div
          className={`grid ${
            hasDecoration
              ? "grid-cols-[80px_1fr] sm:grid-cols-[140px_1fr] lg:grid-cols-[180px_1fr]"
              : "grid-cols-1"
          }`}
        >
          {hasDecoration && (
            <div className="relative bg-white border-r border-[color:var(--afb-cream)]/70">
              <img
                src={data.decoration_url}
                alt=""
                className="absolute inset-0 w-full h-full object-contain object-center p-3 sm:p-4"
                data-testid={`manifesto-decoration-${data.id}`}
              />
            </div>
          )}

          <div className="p-5 sm:p-7 lg:p-9">
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start">
              {data.photo_url ? (
                <div className="w-24 sm:w-28 lg:w-32 mx-auto sm:mx-0">
                  <div className="aspect-square overflow-hidden rounded-full border-2 border-[color:var(--afb-gold)]/40 shadow-sm bg-white">
                    <img
                      src={data.photo_url}
                      alt={fullName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              ) : (
                <div className="hidden sm:block w-1" aria-hidden="true" />
              )}

              <div className="text-center sm:text-left">
                {data.intro_text && (
                  <p className="serif italic text-[15px] sm:text-base lg:text-lg text-afb-ink/80 leading-snug">
                    {data.intro_text}
                    {data.age && (
                      <>
                        {" "}
                        <span className="font-semibold not-italic">
                          {data.age} {/^\d+$/.test(String(data.age)) ? "anni" : ""}
                        </span>
                      </>
                    )}
                  </p>
                )}

                {fullName && (
                  <h2
                    className={`serif italic font-medium text-afb-ink leading-[1.05] mt-2 sm:mt-3 ${
                      compact
                        ? "text-3xl sm:text-4xl"
                        : "text-4xl sm:text-5xl lg:text-6xl"
                    }`}
                  >
                    {fullName}
                  </h2>
                )}

                {data.relation && (
                  <p className="mt-2 sm:mt-3 text-base sm:text-lg text-afb-ink/85 italic">
                    {data.relation}
                  </p>
                )}
              </div>
            </div>

            <div className="my-6 sm:my-8 flex items-center justify-center text-[color:var(--afb-gold)]">
              <span className="h-px flex-1 bg-[color:var(--afb-cream)]" />
              <span className="mx-3 text-lg">❦</span>
              <span className="h-px flex-1 bg-[color:var(--afb-cream)]" />
            </div>

            {data.announce_text && (
              <p className="text-[15px] sm:text-base lg:text-lg leading-relaxed text-afb-ink text-center sm:text-left">
                {data.announce_text}
              </p>
            )}

            {(data.city || data.date_text) && (
              <p className="mt-4 sm:mt-5 text-sm sm:text-base text-afb-ink/80 text-right italic">
                {[data.city, data.date_text].filter(Boolean).join(", ")}
              </p>
            )}

            {data.funeral_text && (
              <p className="mt-5 sm:mt-7 text-[15px] sm:text-base leading-relaxed text-afb-ink whitespace-pre-line">
                {data.funeral_text}
              </p>
            )}

            {data.extra_text && (
              <p className="mt-4 sm:mt-5 text-[15px] sm:text-base leading-relaxed text-afb-ink whitespace-pre-line">
                {data.extra_text}
              </p>
            )}

            {data.facebook_text && (
              <p className="mt-6 sm:mt-8 text-sm sm:text-base text-afb-ink/85 text-center italic">
                {data.facebook_text}
              </p>
            )}

            <div className="mt-6 sm:mt-8 pt-4 border-t border-[color:var(--afb-cream)] flex flex-col sm:flex-row sm:items-end justify-between gap-2 pr-0 sm:pr-24">
              {data.footer_note ? (
                <span className="text-xs text-afb-muted italic">
                  {data.footer_note}
                </span>
              ) : (
                <span />
              )}
              <span className="text-[10px] uppercase tracking-[0.28em] text-afb-gold">
                Onoranze Funebri Barone
              </span>
            </div>
          </div>
        </div>

        {data.show_badge && (
          <img
            src={BADGE_URL}
            alt="Funeral Service"
            className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain"
            data-testid={`manifesto-badge-${data.id}`}
          />
        )}
      </div>
    </article>
  );
}
